import { Box } from '@mui/system';
import { IPost } from '_interfaces/post.interface';
import { IPostComment } from '_interfaces/postComment.interface';
import { IProjectProfiles } from '_interfaces/types/project.types';
import {
  addPostCommentAtom,
  addScheduleFeedPostAtom,
  deletePostCommentAtom,
  deleteScheduleFeedPostAtom,
  scheduleFeedPostsAtom,
  scheduleFeedSearchAtom,
  toggleScheduleFeedPostLikeAtom,
  updatePostCommentAtom,
  updateScheduleFeedPostAtom,
} from 'atoms';
import ActivityFeed from 'components/activity-feed/ActivityFeed';
import { ICustomMentionData } from 'components/inputs/draft-js-input';
import useToast from 'hooks/useToast';
import { useDeletePostWithMedia } from 'hooks/useDeletePostWithMedia';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { FC, useMemo } from 'react';
import { useGetMeQuery } from 'redux-store/api/auth';
import { useAppSelector } from 'redux-store/hooks/useAppSelector';
import { authSelector } from 'redux-store/selector';
import { IPhaseItem } from 'redux-store/types';

interface IScheduleActivityFeed {
  scheduleItem: IPhaseItem | null;
  profiles: IProjectProfiles[];
  projectId: number;
}

export const ScheduleActivityFeed: FC<IScheduleActivityFeed> = ({ scheduleItem, profiles, projectId }) => {
  const { data: userMe } = useGetMeQuery();
  const showToast = useToast();
  const { companyId } = useAppSelector(authSelector);

  const createPost = useSetAtom(addScheduleFeedPostAtom(scheduleItem?.id?.toString()));
  const updatePost = useSetAtom(updateScheduleFeedPostAtom);
  const deletePostAtom = useSetAtom(deleteScheduleFeedPostAtom);
  const { handleDeletePost, deletePostMediaConfirmModal } = useDeletePostWithMedia(
    (postId, mode) => deletePostAtom(postId, mode),
    projectId,
  );
  const createComment = useSetAtom(addPostCommentAtom);
  const updateComment = useSetAtom(updatePostCommentAtom);
  const deleteComment = useSetAtom(deletePostCommentAtom);
  const togglePostLike = useSetAtom(toggleScheduleFeedPostLikeAtom);
  const [searchQuery, setSearchQuery] = useAtom(scheduleFeedSearchAtom);

  const posts = useAtomValue(scheduleFeedPostsAtom(scheduleItem?.id?.toString())) || [];

  const handleCreatePost = async (post: Partial<IPost>) => {
    try {
      await createPost({
        newPost: { ...post, itemName: scheduleItem.title },
        user: {
          userId: userMe?.id?.toString() || '',
          displayName: `${userMe?.firstName} ${userMe?.lastName}` || '',
          avatarUrl: userMe?.avatar || '',
          role: userMe?.roles?.find((role) => role.companyId?.toString() === companyId)?.jobTitle || '',
        },
      });
    } catch (error) {
      showToast({
        type: 'error',
        message: `Error creating post: ${error.message}`,
      });
    }
  };

  const handleUpdatePost = async (post: Partial<IPost>) => {
    try {
      await updatePost({ ...post, itemName: scheduleItem.title });
    } catch (error) {
      showToast({
        type: 'error',
        message: `Error updating post: ${error.message}`,
      });
    }
  };

  const handleCreateComment = async (comment: Partial<IPostComment>, parentPath: string) => {
    try {
      await createComment({
        data: {
          ...comment,
          createdBy: {
            userId: userMe?.id?.toString() || '',
            displayName: `${userMe?.firstName} ${userMe?.lastName}` || '',
            avatarUrl: userMe?.avatar || '',
          },
        },
        parentPath,
      });
    } catch (error) {
      showToast({
        type: 'error',
        message: `Error creating comment: ${error.message}`,
      });
    }
  };

  const handleUpdateComment = async (comment: Partial<IPostComment>) => {
    try {
      await updateComment(comment);
    } catch (error) {
      showToast({
        type: 'error',
        message: `Error updating comment: ${error.message}`,
      });
    }
  };

  const handleTogglePostLike = async (postId: string) => {
    try {
      await togglePostLike({
        postId,
        scheduleItemId: scheduleItem?.id?.toString(),
        userId: userMe?.id?.toString(),
      });
    } catch (error) {
      showToast({
        type: 'error',
        message: `Error liking post: ${error.message}`,
      });
    }
  };

  const onSetSearchQuery = (searchQuery: string) => {
    setSearchQuery(searchQuery);
  };

  const mentionOptions: ICustomMentionData[] = useMemo(() => {
    return profiles.map((profile: IProjectProfiles) => {
      return {
        id: profile.id,
        name: `${profile.firstName} ${profile.lastName}`,
        firstName: profile.firstName,
        lastName: profile.lastName,
        avatar: profile.avatar,
        email: profile.email,
      };
    });
  }, [profiles]);

  return (
    <>
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid #F3F4F9',
        opacity: 1,
        gap: '40px',
      }}
    >
      <ActivityFeed
        posts={posts}
        mentionOptions={mentionOptions}
        searchQuery={searchQuery}
        uploadPath={projectId ? `companies/${companyId}/projects/${projectId}/scheduleFeed` : undefined}
        isInModal
        onCreatePost={handleCreatePost}
        onUpdatePost={handleUpdatePost}
        onCreateComment={handleCreateComment}
        onDeletePost={handleDeletePost}
        onUpdateComment={handleUpdateComment}
        onTogglePostLike={handleTogglePostLike}
        onSetSearchQuery={onSetSearchQuery}
        feedContextType="SCHEDULE_FEED"
        feedContextItemId={scheduleItem?.id?.toString()}
      />
    </Box>
    {deletePostMediaConfirmModal}
    </>
  );
};
